import { useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import api from '../lib/axios';
import VisitorItem from './VisitorItem';
import { VisitorResponse } from '../types';
import { endpoints } from '../constants/endpoints';

interface VisitorListProps {
  selectedVisitorId?: string | null;
  onSelectVisitor: (visitor: VisitorResponse) => void;
}

export default function VisitorList({
  selectedVisitorId,
  onSelectVisitor,
}: VisitorListProps) {
  const queryClient = useQueryClient();
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const markReadMutation = useMutation({
    mutationFn: (visitorId: string) =>
      api.post(endpoints.chat.markRead(visitorId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
    },
  });

  const handleSelectVisitor = (visitor: VisitorResponse) => {
    onSelectVisitor(visitor);
    markReadMutation.mutate(visitor.visitor_id);
  };

  const fetchVisitors = async ({ pageParam = 0 }) => {
    const limit = 20;
    const skip = pageParam * limit;
    const data = (await api.get(
      endpoints.chat.visitors(skip, limit, flaggedOnly),
    )) as VisitorResponse[];
    return {
      data,
      nextPage: data.length === limit ? pageParam + 1 : undefined,
    };
  };

  const { data, fetchNextPage, hasNextPage, isLoading, error } =
    useInfiniteQuery({
      queryKey: ['visitors', { flagged: flaggedOnly }],
      queryFn: fetchVisitors,
      initialPageParam: 0,
      getNextPageParam: (lastPage) => lastPage.nextPage,
    });

  const visitors = data?.pages.flatMap((page) => page.data) || [];

  return (
    <div className="flex w-1/3 flex-col border-r border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2">
        <button
          onClick={() => setFlaggedOnly(!flaggedOnly)}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            flaggedOnly
              ? 'bg-red-100 text-red-700 border border-red-300'
              : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
          }`}
        >
          Flagged
        </button>
      </div>
      <div className="flex-1 overflow-y-auto" id="scrollableDiv">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-gray-500">
            Loading...
          </div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-red-500">
            Error loading visitors: {error.message}
          </div>
        ) : (
          <InfiniteScroll
            dataLength={visitors.length}
            next={fetchNextPage}
            hasMore={!!hasNextPage}
            loader={
              <div className="p-4 text-center text-sm text-gray-500">
                Loading more...
              </div>
            }
            scrollableTarget="scrollableDiv"
            endMessage={
              visitors.length > 0 && (
                <div className="p-4 text-center text-xs text-gray-400">
                  No more visitors
                </div>
              )
            }
          >
            {visitors.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">
                No visitors found
              </div>
            ) : (
              visitors.map((visitor) => (
                <VisitorItem
                  key={visitor.id}
                  visitor={visitor}
                  isSelected={selectedVisitorId === visitor.visitor_id}
                  onClick={() => handleSelectVisitor(visitor)}
                />
              ))
            )}
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}
